import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { LogOut } from 'lucide-react';
import { ButtonSpinner } from './ButtonSpinner';
import {
  consumeImpersonationEnterAnimation,
  finishExitImpersonation,
  getImpersonatedEmail,
  getImpersonatedInitials,
  getPortalDisplayName,
  hasImpersonationTransitionPending,
  IMPERSONATION_TRANSITION_EVENT,
  consumeImpersonationTransitionMeta,
  isImpersonating,
  clearImpersonationTransition,
} from '../impersonationView';

const EASE_CURTAIN = 'cubic-bezier(0.7, 0, 0.2, 1)';
const EASE_SNAP = 'cubic-bezier(0.4, 0, 0.2, 1)';
const EASE_PILL = 'cubic-bezier(0.34, 1.3, 0.5, 1)';

export const ENTER_MS = 1350;
export const EXIT_MS = 1250;
export const PART_MS = 900;
/** Pre-reload overlay — letterbox close before avatar + loader. */
export const PENDING_CLOSE_MS = 550;
/** Safety net — never leave the pre-reload curtain up indefinitely. */
export const PENDING_OVERLAY_MAX_MS = 15000;
/** Keyframe % when enter letterbox panels meet at center — seam content waits until then. */
export const ENTER_CURTAIN_CLOSE_PCT = 32;
/** Post-reload enter — curtains stay closed until session chrome handoff. */
export const ENTER_CURTAIN_PART_PCT = 66;
const EDGE_ENTER_MS = 720;
const EDGE_PULSE_MS = 3200;
const PULSE_MS = 2200;

export const IMPERSONATION_EDGE_BORDER_MIX = 48;
export const IMPERSONATION_EDGE_GLOW_MIX = 14;
export const IMPERSONATION_CORNER_PEAK_MIX = 36;
export const IMPERSONATION_CORNER_MID_MIX = 16;
export const IMPERSONATION_GLOW_PULSE_MIN = 0.72;
export const IMPERSONATION_FRAME_Z_INDEX = 1250;

/** StrictMode remount guard — enter flag must not be consumed twice on one load. */
let enterAnimationConsumed = false;

/** @internal Resets StrictMode enter guard between test runs. */
export function resetImpersonationEnterGuard() {
  enterAnimationConsumed = false;
}

const curtainTopIn = keyframes`
  0% { transform: translateY(-101%); }
  32% { transform: translateY(0); }
  66% { transform: translateY(0); }
  100% { transform: translateY(-101%); }
`;

const curtainBottomIn = keyframes`
  0% { transform: translateY(101%); }
  32% { transform: translateY(0); }
  66% { transform: translateY(0); }
  100% { transform: translateY(101%); }
`;

const curtainTopOpen = keyframes`
  0% { transform: translateY(0); }
  100% { transform: translateY(-101%); }
`;

const curtainBottomOpen = keyframes`
  0% { transform: translateY(0); }
  100% { transform: translateY(101%); }
`;

const curtainTopClose = keyframes`
  0% { transform: translateY(-101%); }
  100% { transform: translateY(0); }
`;

const curtainBottomClose = keyframes`
  0% { transform: translateY(101%); }
  100% { transform: translateY(0); }
`;

const curtainTopEnterFromHold = keyframes`
  0%, ${ENTER_CURTAIN_PART_PCT}% { transform: translateY(0); }
  100% { transform: translateY(-101%); }
`;

const curtainBottomEnterFromHold = keyframes`
  0%, ${ENTER_CURTAIN_PART_PCT}% { transform: translateY(0); }
  100% { transform: translateY(101%); }
`;

const seamReveal = keyframes`
  0%, ${ENTER_CURTAIN_CLOSE_PCT}% { opacity: 0; letter-spacing: 9px; }
  45% { opacity: 1; letter-spacing: 2px; }
  62% { opacity: 1; }
  82%, 100% { opacity: 0; }
`;

const seamRevealFromHold = keyframes`
  0%, 10% { opacity: 0; letter-spacing: 9px; }
  24% { opacity: 1; letter-spacing: 2px; }
  58% { opacity: 1; }
  72%, 100% { opacity: 0; }
`;

const avatarPop = keyframes`
  0% { transform: scale(0.4); opacity: 0; }
  55% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const edgeSpread = keyframes`
  0% {
    opacity: 0;
    box-shadow:
      inset 0 0 0 0 color-mix(in oklch, var(--imp-accent) 0%, transparent),
      inset 0 0 0 0 color-mix(in oklch, var(--imp-accent) 0%, transparent);
  }
  100% {
    opacity: 1;
    box-shadow:
      inset 0 0 0 2px color-mix(in oklch, var(--imp-accent) ${IMPERSONATION_EDGE_BORDER_MIX}%, transparent),
      inset 0 0 48px 8px color-mix(in oklch, var(--imp-accent) ${IMPERSONATION_EDGE_GLOW_MIX}%, transparent);
  }
`;

const cornerBloom = keyframes`
  0% { opacity: 0; transform: scale(0.6); }
  100% { opacity: 1; transform: scale(1); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: ${IMPERSONATION_GLOW_PULSE_MIN}; }
  50% { opacity: 1; }
`;

const riseSoft = keyframes`
  from { transform: translateY(14px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pillRise = keyframes`
  from { transform: translateY(130%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const dotPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 color-mix(in oklch, var(--imp-accent) 55%, transparent);
  }
  70% {
    box-shadow: 0 0 0 7px color-mix(in oklch, var(--imp-accent) 0%, transparent);
  }
`;

const fadeInLate = keyframes`
  0%, 45% { opacity: 0; }
  72% { opacity: 1; }
  100% { opacity: 1; }
`;

const FrameRoot = styled.div`
  --imp-accent: ${({ theme }) => theme.accent};
  --imp-success: ${({ theme }) => theme.success || 'oklch(0.62 0.15 158)'};
  position: fixed;
  inset: 0;
  z-index: ${IMPERSONATION_FRAME_Z_INDEX};
  pointer-events: none;
  overflow: hidden;
`;

const CurtainLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 70;
  overflow: hidden;
  pointer-events: auto;
`;

const CurtainPanel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 50%;
  pointer-events: none;

  &.top {
    top: 0;
    background: linear-gradient(180deg, oklch(0.14 0.03 268), oklch(0.16 0.028 268));
  }

  &.bottom {
    bottom: 0;
    background: linear-gradient(0deg, oklch(0.14 0.03 268), oklch(0.16 0.028 268));
  }

  ${({ $variant }) => {
    if ($variant === 'enter') {
      return css`
        &.top { animation: ${curtainTopIn} ${ENTER_MS}ms ${EASE_CURTAIN} both; }
        &.bottom { animation: ${curtainBottomIn} ${ENTER_MS}ms ${EASE_CURTAIN} both; }
      `;
    }
    if ($variant === 'enterFromHold') {
      return css`
        &.top { animation: ${curtainTopEnterFromHold} ${ENTER_MS}ms ${EASE_CURTAIN} both; }
        &.bottom { animation: ${curtainBottomEnterFromHold} ${ENTER_MS}ms ${EASE_CURTAIN} both; }
      `;
    }
    if ($variant === 'part') {
      return css`
        &.top { animation: ${curtainTopOpen} 0.55s ${EASE_SNAP} both; }
        &.bottom { animation: ${curtainBottomOpen} 0.55s ${EASE_SNAP} both; }
      `;
    }
    if ($variant === 'exit') {
      return css`
        &.top { animation: ${curtainTopClose} 0.55s cubic-bezier(0.5, 0, 0.2, 1) both; }
        &.bottom { animation: ${curtainBottomClose} 0.55s cubic-bezier(0.5, 0, 0.2, 1) both; }
      `;
    }
    if ($variant === 'hold') {
      return css`
        &.top { transform: translateY(0); }
        &.bottom { transform: translateY(0); }
      `;
    }
    return '';
  }}
`;

const SeamCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 71;
  pointer-events: none;

  ${({ $animate, $enterFromHold }) => {
    if (!$animate) return '';
    if ($enterFromHold) {
      return css`
        animation: ${seamRevealFromHold} ${ENTER_MS}ms ease both;
      `;
    }
    return css`
      animation: ${seamReveal} ${ENTER_MS}ms ease both;
    `;
  }}
`;

const EnterAvatar = styled.div`
  width: 116px;
  height: 116px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 44px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 28px;
  background: linear-gradient(
    135deg,
    color-mix(in oklch, var(--imp-accent) 88%, oklch(0.2 0.05 274)),
    color-mix(in oklch, var(--imp-accent) 65%, #fff)
  );
  box-shadow:
    0 0 0 10px color-mix(in oklch, var(--imp-accent) 12%, transparent),
    0 0 70px color-mix(in oklch, var(--imp-accent) 45%, transparent);

  ${({ $animate, $enterFromHold }) =>
    $animate &&
    css`
      animation: ${avatarPop} 0.7s ${$enterFromHold ? 120 : Math.round(ENTER_MS * (ENTER_CURTAIN_CLOSE_PCT / 100))}ms ${EASE_PILL} both;
    `}
`;

const SeamEyebrow = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: color-mix(in oklch, var(--imp-accent) 75%, #fff);
  margin-bottom: 10px;
`;

const SeamName = styled.div`
  font-size: clamp(24px, 5vw, 34px);
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.5px;
  line-height: 1.15;
`;

const SeamEmail = styled.div`
  font-size: 14px;
  color: color-mix(in oklch, #fff 55%, transparent);
  margin-top: 7px;
`;

const ExitMessage = styled.div`
  animation: ${fadeInLate} ${EXIT_MS}ms both;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ExitTitle = styled.div`
  font-size: 19px;
  font-weight: 800;
  color: #fff;
  margin-top: 20px;
`;

const ExitSub = styled.div`
  font-size: 14px;
  color: color-mix(in oklch, #fff 60%, transparent);
  margin-top: 6px;
`;

const SessionFrameLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 40;
  pointer-events: none;
`;

const EdgeGlow = styled.div`
  position: absolute;
  inset: 0;
  box-shadow:
    inset 0 0 0 2px color-mix(in oklch, var(--imp-accent) ${IMPERSONATION_EDGE_BORDER_MIX}%, transparent),
    inset 0 0 48px 8px color-mix(in oklch, var(--imp-accent) ${IMPERSONATION_EDGE_GLOW_MIX}%, transparent);

  ${({ $enter }) =>
    $enter &&
    css`
      animation: ${edgeSpread} ${EDGE_ENTER_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
    `}

  ${({ $pulse, $enter }) =>
    $pulse &&
    !$enter &&
    css`
      animation: ${glowPulse} ${EDGE_PULSE_MS}ms ease-in-out infinite;
    `}
`;

const cornerBase = css`
  position: absolute;
  width: 160px;
  height: 160px;
  pointer-events: none;
  background: radial-gradient(
    circle at var(--cx) var(--cy),
    color-mix(in oklch, var(--imp-accent) ${IMPERSONATION_CORNER_PEAK_MIX}%, transparent) 0%,
    color-mix(in oklch, var(--imp-accent) ${IMPERSONATION_CORNER_MID_MIX}%, transparent) 38%,
    transparent 68%
  );

  ${({ $enter, $delay }) =>
    $enter &&
    css`
      animation: ${cornerBloom} ${EDGE_ENTER_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${$delay}ms forwards;
    `}

  ${({ $pulse, $enter }) =>
    $pulse &&
    !$enter &&
    css`
      animation: ${glowPulse} ${EDGE_PULSE_MS}ms ease-in-out infinite;
    `}
`;

const CornerTL = styled.div`${cornerBase} top: 0; left: 0; --cx: 0%; --cy: 0%; transform-origin: top left;`;
const CornerTR = styled.div`${cornerBase} top: 0; right: 0; --cx: 100%; --cy: 0%; transform-origin: top right;`;
const CornerBL = styled.div`${cornerBase} bottom: 0; left: 0; --cx: 0%; --cy: 100%; transform-origin: bottom left;`;
const CornerBR = styled.div`${cornerBase} bottom: 0; right: 0; --cx: 100%; --cy: 100%; transform-origin: bottom right;`;

/** Centers chrome in the main column — excludes the sidebar grid track. */
const MainContentAnchor = styled.div`
  position: fixed;
  left: var(--sidebar-w, 248px);
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;

  @media (max-width: 768px) {
    left: 0;
  }
`;

const TopBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--imp-accent);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 18px;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 6px 18px color-mix(in oklch, var(--imp-accent) 30%, transparent);
  pointer-events: none;
  white-space: nowrap;
  max-width: min(420px, calc(100vw - var(--sidebar-w, 248px) - 32px));
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    max-width: min(420px, calc(100vw - 32px));
  }

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${riseSoft} 0.5s 0.12s ${EASE_SNAP} both;
    `}

  .badge-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    flex-shrink: 0;
  }
`;

const DockAnchor = styled(MainContentAnchor)`
  bottom: 26px;
  z-index: 50;

  @media (max-width: 520px) {
    bottom: 16px;
  }
`;

const TopBadgeAnchor = styled(MainContentAnchor)`
  top: 0;
  z-index: 41;
  transform: translateX(${({ $collapsed }) => ($collapsed ? '14px' : '-28px')});

  @media (max-width: 768px) {
    transform: translateX(${({ $collapsed }) => ($collapsed ? '8px' : '-12px')});
  }
`;

const DockPill = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: color-mix(in oklch, oklch(0.21 0.018 268) 88%, transparent);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid color-mix(in oklch, #fff 10%, transparent);
  border-radius: ${({ theme }) => theme.rPill || '999px'};
  padding: 6px 6px 6px 8px;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.32),
    0 4px 12px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 color-mix(in oklch, #fff 8%, transparent);
  pointer-events: auto;
  max-width: min(520px, calc(100vw - var(--sidebar-w, 248px) - 32px));

  @media (max-width: 768px) {
    max-width: min(520px, calc(100vw - 32px));
  }

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${pillRise} 0.55s 0.1s ${EASE_PILL} both;
    `}

  .pill-avatar-wrap {
    position: relative;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }

  .pill-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.02em;
    color: #fff;
    background: color-mix(in oklch, ${({ theme }) => theme.accent} 78%, oklch(0.35 0.08 274));
    box-shadow: inset 0 1px 0 color-mix(in oklch, #fff 22%, transparent);
  }

  .pill-dot {
    position: absolute;
    right: -1px;
    bottom: -1px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: var(--imp-success);
    border: 2px solid oklch(0.16 0.02 268);
    animation: ${dotPulse} ${PULSE_MS}ms ease-in-out infinite;
  }

  .pill-eyebrow {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: color-mix(in oklch, ${({ theme }) => theme.accent} 55%, oklch(0.78 0.04 274));
    line-height: 1;
  }

  .pill-email {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
  }

  .pill-exit {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 38px;
    padding: 0 16px;
    background: #fff;
    color: oklch(0.21 0.018 268);
    border: none;
    border-radius: ${({ theme }) => theme.rPill || '999px'};
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
    flex-shrink: 0;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  @media (max-width: 520px) {
    .pill-exit span { display: none; }
  }
`;

function resolveInitialPhase() {
  if (enterAnimationConsumed) return 'idle';
  if (!consumeImpersonationEnterAnimation()) return 'idle';
  enterAnimationConsumed = true;
  consumeImpersonationTransitionMeta();
  // Seam avatar already shown on the pre-reload overlay — only part curtains + session chrome.
  return 'parting';
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false,
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return undefined;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function EnterSeamContent({ displayName, email, initials, animate, enterFromHold = false }) {
  return (
    <SeamCenter $animate={animate} $enterFromHold={enterFromHold}>
      <EnterAvatar $animate={animate} $enterFromHold={enterFromHold} aria-hidden="true">{initials}</EnterAvatar>
      <SeamEyebrow>Viewing as</SeamEyebrow>
      <SeamName>{displayName}</SeamName>
      {email && <SeamEmail>{email}</SeamEmail>}
    </SeamCenter>
  );
}

function SessionChrome({
  displayName,
  email,
  initials,
  animate,
  isPulsing,
  onExit,
  exitDisabled,
  sidebarCollapsed,
}) {
  return (
    <>
      <SessionFrameLayer data-testid="impersonation-session-frame" aria-hidden="true">
        <EdgeGlow className="frame-edge" $enter={animate} $pulse={isPulsing} />
        <CornerTL className="frame-corner" $enter={animate} $pulse={isPulsing} $delay={0} />
        <CornerTR className="frame-corner" $enter={animate} $pulse={isPulsing} $delay={80} />
        <CornerBL className="frame-corner" $enter={animate} $pulse={isPulsing} $delay={160} />
        <CornerBR className="frame-corner" $enter={animate} $pulse={isPulsing} $delay={240} />
      </SessionFrameLayer>
      <TopBadgeAnchor $collapsed={sidebarCollapsed}>
        <TopBadge $animate={animate} role="status">
          <span className="badge-dot" aria-hidden="true" />
          Viewing as {displayName}
        </TopBadge>
      </TopBadgeAnchor>
      <DockAnchor>
        <DockPill className="frame-dock" $animate={animate} role="status">
          <div className="pill-avatar-wrap">
            <div className="pill-avatar" aria-hidden="true">{initials}</div>
            <span className="pill-dot" aria-hidden="true" />
          </div>
          <div style={{ lineHeight: 1.3, marginRight: 6, minWidth: 0 }}>
            <div className="pill-eyebrow">Viewing as</div>
            <div className="pill-email" title={email}>{email}</div>
          </div>
          <button
            type="button"
            className="pill-exit"
            onClick={onExit}
            disabled={exitDisabled}
          >
            <LogOut size={16} strokeWidth={2.25} aria-hidden="true" />
            <span>Exit view</span>
          </button>
        </DockPill>
      </DockAnchor>
    </>
  );
}

/**
 * Full-viewport impersonation curtain — enter / part / idle / exit phases.
 */
function ImpersonationFrame({ sidebarCollapsed = false }) {
  const email = getImpersonatedEmail();
  const displayName = getPortalDisplayName();
  const initials = getImpersonatedInitials();
  const reducedMotion = useReducedMotion();
  const [phase, setPhase] = useState(resolveInitialPhase);
  const [sessionEnter, setSessionEnter] = useState(false);

  useEffect(() => {
    if (!reducedMotion) return undefined;
    if (phase === 'exiting') {
      finishExitImpersonation();
      return undefined;
    }
    if (phase === 'parting') {
      setPhase('idle');
    }
    return undefined;
  }, [reducedMotion, phase]);

  useEffect(() => {
    if (reducedMotion) return undefined;
    if (phase === 'parting') {
      const timer = window.setTimeout(() => {
        setSessionEnter(true);
        setPhase('idle');
      }, PART_MS);
      return () => window.clearTimeout(timer);
    }
    if (phase === 'exiting') {
      const timer = window.setTimeout(() => finishExitImpersonation(), EXIT_MS);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [phase, reducedMotion]);

  const showSession = phase === 'idle';
  const sessionAnimate = sessionEnter;

  const handleExit = () => {
    if (phase === 'exiting') return;
    if (reducedMotion) {
      finishExitImpersonation();
      return;
    }
    setPhase('exiting');
  };

  return (
    <FrameRoot data-testid="impersonation-frame" data-phase={phase}>
      {showSession && (
        <SessionChrome
          displayName={displayName}
          email={email}
          initials={initials}
          animate={sessionAnimate}
          isPulsing={phase === 'idle'}
          onExit={handleExit}
          exitDisabled={phase === 'exiting'}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}

      {phase === 'parting' && (
        <CurtainLayer data-testid="impersonation-curtain-part">
          <CurtainPanel
            className="top"
            data-testid="impersonation-letterbox-top"
            $variant="part"
            aria-hidden="true"
          />
          <CurtainPanel
            className="bottom"
            data-testid="impersonation-letterbox-bottom"
            $variant="part"
            aria-hidden="true"
          />
        </CurtainLayer>
      )}

      {phase === 'exiting' && (
        <CurtainLayer data-testid="impersonation-curtain-exit" role="status" aria-live="polite">
          <CurtainPanel className="top" $variant="exit" aria-hidden="true" />
          <CurtainPanel className="bottom" $variant="exit" aria-hidden="true" />
          <SeamCenter>
            <ExitMessage>
              <ButtonSpinner size={40} className="transition-spinner" />
              <ExitTitle>Returning to your account</ExitTitle>
              <ExitSub>Restoring your admin session…</ExitSub>
            </ExitMessage>
          </SeamCenter>
        </CurtainLayer>
      )}
    </FrameRoot>
  );
}

/**
 * Pre-reload curtain — letterbox closes first, then avatar + loader while plan fetch runs.
 */
export function ImpersonationPendingOverlay() {
  const reducedMotion = useReducedMotion();
  const [visible, setVisible] = useState(() => {
    if (isImpersonating()) {
      clearImpersonationTransition();
      return false;
    }
    return hasImpersonationTransitionPending();
  });
  const [showContent, setShowContent] = useState(() => reducedMotion);
  const [meta, setMeta] = useState(() => {
    try {
      const raw = sessionStorage.getItem('studyak_impersonation_transition');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (isImpersonating()) {
      clearImpersonationTransition();
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return undefined;

    const dismissIfComplete = () => {
      if (isImpersonating() || !hasImpersonationTransitionPending()) {
        setVisible(false);
      }
    };

    dismissIfComplete();
    const pollId = window.setInterval(dismissIfComplete, 250);
    const timeoutId = window.setTimeout(() => {
      if (!isImpersonating()) {
        clearImpersonationTransition();
        setVisible(false);
      }
    }, PENDING_OVERLAY_MAX_MS);

    return () => {
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
    };
  }, [visible]);

  useEffect(() => {
    const onStart = () => {
      setVisible(true);
      setShowContent(reducedMotion);
      try {
        const raw = sessionStorage.getItem('studyak_impersonation_transition');
        setMeta(raw ? JSON.parse(raw) : null);
      } catch {
        setMeta(null);
      }
    };
    window.addEventListener(IMPERSONATION_TRANSITION_EVENT, onStart);
    return () => window.removeEventListener(IMPERSONATION_TRANSITION_EVENT, onStart);
  }, [reducedMotion]);

  useEffect(() => {
    if (!visible || reducedMotion) return undefined;
    setShowContent(false);
    const timer = window.setTimeout(() => setShowContent(true), PENDING_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [visible, reducedMotion]);

  if (!visible) return null;

  const displayName = meta?.displayName || meta?.email?.split('@')[0] || 'User';
  const email = meta?.email || '';
  const initials = email
    ? email.split('@')[0].slice(0, 2).toUpperCase()
    : displayName.slice(0, 2).toUpperCase();
  const curtainVariant = reducedMotion ? 'hold' : 'close';

  return (
    <FrameRoot
      data-testid="impersonation-pending-overlay"
      data-phase={showContent ? 'loading' : 'closing'}
      style={{ pointerEvents: 'auto' }}
    >
      <CurtainLayer>
        <CurtainPanel
          className="top"
          data-testid="impersonation-letterbox-top"
          $variant={curtainVariant}
          aria-hidden="true"
        />
        <CurtainPanel
          className="bottom"
          data-testid="impersonation-letterbox-bottom"
          $variant={curtainVariant}
          aria-hidden="true"
        />
        {showContent && (
          <>
            <EnterSeamContent displayName={displayName} email={email} initials={initials} animate={false} />
            <SeamCenter style={{ top: '62%' }}>
              <ButtonSpinner size={26} />
            </SeamCenter>
          </>
        )}
      </CurtainLayer>
    </FrameRoot>
  );
}

export default ImpersonationFrame;
